import type { BabylonState } from "../../state";
import { pickScenePoint } from "../picking/pickScenePoint";

import { WAKE_THRESHOLD_PX } from "./attachLatchedDrag";

/**
 * A left press in measure mode, tracked until it resolves to a click or a
 * drag.
 */
interface Press {
  pointerId: number;
  /** Canvas-relative CSS px of the `pointerdown`. */
  x: number;
  y: number;
  /**
   * Set once the pointer has been {@link WAKE_THRESHOLD_PX} away from
   * `(x, y)`, and never cleared: a gesture that has been a drag stays one.
   */
  dragged: boolean;
}

/** Whether `e` is far enough from a press's origin to count as a drag. */
function isDragDistance(press: Press, e: PointerEvent): boolean {
  return (
    Math.hypot(e.offsetX - press.x, e.offsetY - press.y) >= WAKE_THRESHOLD_PX
  );
}

/**
 * Mirrors the cursor into `state.input.tool.pointer` (canvas-relative CSS
 * px, the coordinate space picking takes) and resolves measure-mode clicks
 * into `pendingPins`.
 *
 * Hovering is write-only: this handler only records the position, and the
 * render loop picks against it once per frame (`reportToolHover`), so pick
 * cost tracks frame rate rather than pointer-event rate.
 *
 * A click is picked here, synchronously, against the frame already on
 * screen — the one the user aimed at. Deferring to the next frame would
 * resolve the pick after the camera has moved, baking that offset into a
 * measurement endpoint and letting a double-click's two picks differ past
 * `pinPoint`'s exact-equality guard. The camera nudge from click jitter is
 * accepted; suppressing it would take a dead zone in Babylon's orbit
 * input, not this handler.
 *
 * A click is a left press-and-release that never moved
 * `WAKE_THRESHOLD_PX` from its origin — the distance at which the
 * latched-drag camera handlers wake. Never, not just at release: `dragged`
 * latches, so an orbit that wanders out and returns to its start still
 * reads as a drag. One press is tracked at a time: a second finger landing
 * mid-gesture is part of a pinch, not a new click. Cursor tracking is
 * mode-blind, so the tool turned on mid-hover answers immediately.
 * Effectively inert for a state built without tool input.
 *
 * Returns a cleanup that detaches every listener.
 */
export function attachToolPointerInput(state: BabylonState): () => void {
  const canvas = state.context.canvas;
  let press: Press | undefined;

  const onPointerMove = (e: PointerEvent) => {
    if (press && press.pointerId === e.pointerId) {
      press.dragged ||= isDragDistance(press, e);
    }
    const tool = state.input.tool;
    if (!tool) {
      return;
    }
    tool.pointer = { x: e.offsetX, y: e.offsetY };
  };

  const onPointerLeave = (e: PointerEvent) => {
    // A press that leaves the canvas gets no `pointerup` here, so abandon
    // it rather than let it pair with a later release. Only the press that
    // actually left, though: `pointerleave` fires per pointer (every touch
    // lift gets one), so a pointer-id-blind handler would let a second
    // finger's leave destroy the first finger's press.
    if (press && press.pointerId === e.pointerId) {
      press = undefined;
    }
    const tool = state.input.tool;
    if (!tool) {
      return;
    }
    // The cursor is a single position, so it clears no matter which
    // pointer left: whichever it was, nothing remains over the canvas for
    // hover picking to answer.
    tool.pointer = undefined;
  };

  const onPointerDown = (e: PointerEvent) => {
    const tool = state.input.tool;
    if (press !== undefined || !tool || tool.mode !== "measure") {
      return;
    }
    if (e.button !== 0) {
      return;
    }
    press = {
      pointerId: e.pointerId,
      x: e.offsetX,
      y: e.offsetY,
      dragged: false,
    };
  };

  const onPointerUp = (e: PointerEvent) => {
    const tool = state.input.tool;
    const gesture = press;
    if (!tool || !gesture || gesture.pointerId !== e.pointerId) {
      return;
    }
    press = undefined;
    // Chorded buttons deliver one `pointerup`, for the button released
    // last: a left press that ends on a right-button release is a
    // context-menu attempt, not a pin.
    if (e.button !== 0) {
      return;
    }
    // `pointerup` can carry motion since the last `pointermove`, so the
    // release position gets the same distance test the moves did.
    if (
      tool.mode !== "measure" ||
      gesture.dragged ||
      isDragDistance(gesture, e)
    ) {
      return;
    }
    const pick = pickScenePoint(state, e.offsetX, e.offsetY);
    if (!pick) {
      // A measurement endpoint means a point on the data, so a click on
      // the background pins nothing.
      return;
    }
    (tool.pendingPins ??= []).push(pick.dataPoint);
  };

  const onPointerCancel = (e: PointerEvent) => {
    if (press && press.pointerId === e.pointerId) {
      press = undefined;
    }
  };

  canvas.addEventListener("pointermove", onPointerMove);
  canvas.addEventListener("pointerleave", onPointerLeave);
  canvas.addEventListener("pointerdown", onPointerDown);
  canvas.addEventListener("pointerup", onPointerUp);
  canvas.addEventListener("pointercancel", onPointerCancel);

  return () => {
    canvas.removeEventListener("pointermove", onPointerMove);
    canvas.removeEventListener("pointerleave", onPointerLeave);
    canvas.removeEventListener("pointerdown", onPointerDown);
    canvas.removeEventListener("pointerup", onPointerUp);
    canvas.removeEventListener("pointercancel", onPointerCancel);
  };
}
